import { useAuth } from "@clerk/clerk-react";
import { useState } from "react";
import { trackEvent } from "../lib/analytics";
import { getPlanButtonUrl } from "../lib/flow";
import type { LicenseState } from "../lib/stripe/license";
import { redirectToCheckout } from "../lib/stripe/license";

interface UpgradeSectionProps {
	license: LicenseState | null;
	isLoading: boolean;
	portalUrl: string | null;
	/** "home": ログイン/マイページ遷移ボタンのみ表示（デフォルト）
	 *  "mypage": Checkout導線あり（ログイン済み前提） */
	context?: "home" | "mypage";
}

export function UpgradeSection({
	license,
	isLoading,
	portalUrl,
	context = "home",
}: UpgradeSectionProps) {
	const { isSignedIn, getToken } = useAuth();
	const [checkoutLoading, setCheckoutLoading] = useState<
		"monthly" | "yearly" | null
	>(null);
	const [error, setError] = useState<string | null>(null);

	const handleCheckout = async (plan: "monthly" | "yearly") => {
		setCheckoutLoading(plan);
		setError(null);
		const planValue = plan === "yearly" ? 5000 : 500;
		trackEvent("select_plan", {
			plan_name: plan,
			value: planValue,
			currency: "JPY",
		});
		try {
			const token = await getToken();
			trackEvent("begin_checkout", {
				plan_name: plan,
				value: planValue,
				currency: "JPY",
			});
			await redirectToCheckout(plan, token);
		} catch {
			setError(
				"決済ページへの遷移に失敗しました。しばらくしてから再度お試しください。",
			);
			setCheckoutLoading(null);
		}
	};

	// 有料ユーザー向けUI
	if (license?.active) {
		return (
			<section id="upgrade" className="py-12 px-4">
				<div className="max-w-md mx-auto text-center">
					<div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-8">
						<div className="text-3xl mb-3">✨</div>
						<h2 className="text-xl font-bold text-gray-900 mb-2">
							プロプラン利用中
						</h2>
						<p className="text-sm text-gray-600 mb-1">
							プラン:{" "}
							<strong>{license.plan === "yearly" ? "年額" : "月額"}</strong>
						</p>
						{license.expires_at && (
							<p className="text-sm text-gray-500 mb-6">
								次回更新:{" "}
								{new Date(license.expires_at).toLocaleDateString("ja-JP")}
							</p>
						)}
						{portalUrl && (
							<a
								href={portalUrl}
								className="inline-flex items-center text-sm text-blue-700 hover:underline"
								target="_blank"
								rel="noopener noreferrer"
							>
								サブスクリプション管理 →
							</a>
						)}
					</div>
				</div>
			</section>
		);
	}

	// プラン紹介セクション（共通）
	const planFeatures = [
		"無制限枚数",
		"フル解像度出力",
		"透かしなし",
		"バッチ処理・ZIP出力",
		"全プラットフォーム対応",
	];

	return (
		<section
			id="upgrade"
			className="py-16 px-4 bg-gradient-to-b from-white to-blue-50"
		>
			<div className="max-w-3xl mx-auto text-center">
				<div className="text-4xl mb-4">🚀</div>
				<h2 className="text-3xl font-bold text-gray-900 mb-3">
					プロプランで制限なし
				</h2>
				<p className="text-lg text-gray-600 mb-10">
					フル解像度・透かしなし・毎日無制限で使えるプロプラン
				</p>

				{error && (
					<div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
						{error}
					</div>
				)}

				{context === "home" ? (
					// トップページ用: ログイン/マイページ遷移ボタンのみ
					<div className="max-w-md mx-auto">
						<div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
							<ul className="text-sm text-gray-600 text-left space-y-2 mb-8">
								{planFeatures.map((f) => (
									<li key={f} className="flex items-center gap-2">
										<span className="text-green-500">✓</span> {f}
									</li>
								))}
							</ul>
							<p className="text-2xl font-bold text-blue-600 mb-1">
								¥500
								<span className="text-base font-normal text-gray-500">
									/月〜
								</span>
							</p>
							<p className="text-sm text-gray-500 mb-6">
								年額¥5,000（2ヶ月分お得）
							</p>
							<a
								href={getPlanButtonUrl(isSignedIn ?? false)}
								className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-xl transition-colors text-center"
							>
								{isSignedIn
									? "マイページでプランを確認"
									: "ログインしてプランを見る"}
							</a>
						</div>
					</div>
				) : (
					// マイページ用: Checkout導線あり
					<div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
						{/* 月額プラン */}
						<div className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-blue-300 transition-colors">
							<h3 className="text-lg font-bold text-gray-900 mb-1">
								月額プラン
							</h3>
							<p className="text-4xl font-bold text-blue-600 mb-1">
								¥500
								<span className="text-lg font-normal text-gray-500">/月</span>
							</p>
							<p className="text-sm text-gray-500 mb-6">いつでも解約可能</p>
							<ul className="text-sm text-gray-600 text-left space-y-2 mb-8">
								{planFeatures.map((f) => (
									<li key={f} className="flex items-center gap-2">
										<span className="text-green-500">✓</span> {f}
									</li>
								))}
							</ul>
							<button
								type="button"
								onClick={() => handleCheckout("monthly")}
								disabled={checkoutLoading !== null || isLoading}
								className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium py-3 px-6 rounded-xl transition-colors"
							>
								{checkoutLoading === "monthly"
									? "処理中..."
									: "月額プランを始める"}
							</button>
						</div>

						{/* 年額プラン */}
						<div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 text-white relative overflow-hidden">
							<div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
								お得
							</div>
							<h3 className="text-lg font-bold mb-1">年額プラン</h3>
							<p className="text-4xl font-bold mb-1">
								¥5,000
								<span className="text-lg font-normal opacity-80">/年</span>
							</p>
							<p className="text-sm opacity-80 mb-6">2ヶ月分お得（¥417/月）</p>
							<ul className="text-sm text-left space-y-2 mb-8">
								{planFeatures.map((f) => (
									<li key={f} className="flex items-center gap-2">
										<span className="text-yellow-300">✓</span> {f}
									</li>
								))}
							</ul>
							<button
								type="button"
								onClick={() => handleCheckout("yearly")}
								disabled={checkoutLoading !== null || isLoading}
								className="w-full bg-white hover:bg-gray-50 disabled:opacity-60 text-blue-700 font-medium py-3 px-6 rounded-xl transition-colors"
							>
								{checkoutLoading === "yearly"
									? "処理中..."
									: "年額プランを始める"}
							</button>
						</div>
					</div>
				)}

				<p className="mt-6 text-sm text-gray-400">
					クレジットカード決済。いつでも解約可能。Stripeにより安全に処理されます。
				</p>

				{/* 購入済みユーザー向けログインリンク（homeコンテキストのみ） */}
				{context === "home" && (
					<div className="mt-10 pt-8 border-t border-gray-200 text-center">
						<p className="text-sm text-gray-500">
							購入済みの方は{" "}
							<a
								href="/sign-in"
								className="text-blue-600 hover:underline font-medium"
							>
								ログイン
							</a>{" "}
							してマイページでご確認ください
						</p>
					</div>
				)}
			</div>
		</section>
	);
}
